import React, { useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { curveMonotoneX, line } from 'd3-shape';
import { scaleLinear } from 'd3-scale';

import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { ChartHScroll, formatCurrency, formatK, MIN_PX_PER_CALENDAR_YEAR, useChartPlotWidth } from './d3';
import { SvgChartCanvas } from './web/SvgChartCanvas.web';

type SeriesPoint = { year: number; balance: number };

type DualLineChartProps = {
  seriesA: SeriesPoint[];
  seriesB: SeriesPoint[];
  labelA: string;
  labelB: string;
  colorA?: string;
  colorB?: string;
  crossoverYear?: number;
  height?: number;
};

const MARGIN = { top: 20, right: 14, bottom: 28, left: 44 };
const HIT_MIN = 20;

function balanceAtYear(series: SeriesPoint[], year: number): number {
  let last = series[0]?.balance ?? 0;
  for (const p of series) {
    if (p.year === year) return p.balance;
    if (p.year > year) break;
    last = p.balance;
  }
  return last;
}

export function DualLineChart({
  seriesA,
  seriesB,
  labelA,
  labelB,
  colorA = colors.warning,
  colorB = colors.accent,
  crossoverYear,
  height = 200,
}: DualLineChartProps) {
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  const all = [...seriesA, ...seriesB];
  const minYear = all.length > 0 ? Math.min(...all.map((p) => p.year)) : 0;
  const maxYear = all.length > 0 ? Math.max(...all.map((p) => p.year)) : 0;
  const maxY = Math.max(...all.map((p) => p.balance), 1);
  const { plotWidth: width, scrolls, onLayout } = useChartPlotWidth({
    unitCount: Math.max(maxYear - minYear, 1),
    minPxPerUnit: MIN_PX_PER_CALENDAR_YEAR,
    leftMargin: MARGIN.left,
    rightMargin: MARGIN.right,
  });

  const model = useMemo(() => {
    if (all.length === 0 || width <= 0) {
      return {
        pathA: '',
        pathB: '',
        dots: [] as {
          year: number;
          x: number;
          yA: number;
          yB: number;
        }[],
        yTicks: [] as { value: number; y: number }[],
        xTicks: [] as { year: number; x: number }[],
        crossoverX: null as number | null,
      };
    }

    const innerWidth = width - MARGIN.left - MARGIN.right;
    const innerHeight = height - MARGIN.top - MARGIN.bottom;

    const xScale = scaleLinear()
      .domain([minYear, maxYear])
      .range([MARGIN.left, MARGIN.left + Math.max(innerWidth, 1)]);

    const yScale = scaleLinear()
      .domain([0, maxY])
      .nice()
      .range([MARGIN.top + Math.max(innerHeight, 1), MARGIN.top]);

    const lineGen = line<SeriesPoint>()
      .x((d) => xScale(d.year))
      .y((d) => yScale(d.balance))
      .curve(curveMonotoneX);

    const years = Array.from(
      new Set([...seriesA, ...seriesB].map((p) => p.year)),
    ).sort((a, b) => a - b);

    const yTickValues = yScale.ticks(4);
    const maxTicks = Math.max(4, Math.floor(innerWidth / 56));
    const xTickYears =
      years.length <= maxTicks
        ? years
        : years.filter(
            (_, index) =>
              index % Math.ceil(years.length / maxTicks) === 0 ||
              index === years.length - 1,
          );

    return {
      pathA: lineGen(seriesA) ?? '',
      pathB: lineGen(seriesB) ?? '',
      dots: years.map((year) => ({
        year,
        x: xScale(year),
        yA: yScale(balanceAtYear(seriesA, year)),
        yB: yScale(balanceAtYear(seriesB, year)),
      })),
      yTicks: yTickValues.map((value) => ({ value, y: yScale(value) })),
      xTicks: xTickYears.map((year) => ({ year, x: xScale(year) })),
      crossoverX: crossoverYear != null ? xScale(crossoverYear) : null,
    };
  }, [width, height, seriesA, seriesB, minYear, maxYear, maxY, crossoverYear, all.length]);

  const activeYear =
    selectedYear != null && model.dots.some((d) => d.year === selectedYear)
      ? selectedYear
      : crossoverYear != null && model.dots.some((d) => d.year === crossoverYear)
        ? crossoverYear
        : model.dots[model.dots.length - 1]?.year ?? null;

  const snapshot =
    activeYear != null
      ? {
          year: activeYear,
          a: balanceAtYear(seriesA, activeYear),
          b: balanceAtYear(seriesB, activeYear),
        }
      : null;

  const gap = snapshot != null ? snapshot.b - snapshot.a : 0;
  const hitWidth =
    model.dots.length > 1
      ? Math.max(HIT_MIN, model.dots[1].x - model.dots[0].x)
      : HIT_MIN * 1.5;

  if (all.length === 0) {
    return (
      <View style={styles.empty}>
        <Text style={styles.emptyText}>No projection data yet</Text>
      </View>
    );
  }

  return (
    <View>
      <Text style={styles.hint}>Balance over time · click a year for a snapshot</Text>
      <ChartHScroll
        height={height}
        plotWidth={width}
        scrolls={scrolls}
        onLayout={onLayout}
        style={styles.chartArea}
        axisWidth={MARGIN.left}
        axis={model.yTicks.map((tick) => (
          <Text
            key={`y-${tick.value}`}
            style={[styles.yTick, { top: tick.y - 7 }]}
          >
            {formatK(tick.value)}
          </Text>
        ))}
      >
        {width > 0 && (
          <>
            <SvgChartCanvas
              width={width}
              height={height}
              style={{ pointerEvents: 'none' }}
            >
              {model.yTicks.map((tick, i) => (
                <line
                  key={`grid-${i}`}
                  x1={MARGIN.left}
                  y1={tick.y}
                  x2={width - MARGIN.right}
                  y2={tick.y}
                  stroke={colors.border}
                  strokeWidth={1}
                  opacity={0.45}
                />
              ))}
              {model.crossoverX != null && (
                <>
                  <line
                    x1={model.crossoverX}
                    y1={MARGIN.top}
                    x2={model.crossoverX}
                    y2={height - MARGIN.bottom}
                    stroke="rgba(244, 247, 251, 0.35)"
                    strokeWidth={1.5}
                  />
                  <circle cx={model.crossoverX} cy={MARGIN.top + 8} r={4} fill={colors.text} />
                </>
              )}
              <path d={model.pathA} fill="none" stroke={colorA} strokeWidth={2.5} />
              <path d={model.pathB} fill="none" stroke={colorB} strokeWidth={2.5} />
              {model.dots.map((dot) => {
                const selected = activeYear === dot.year;
                return (
                  <g key={dot.year}>
                    <circle
                      cx={dot.x}
                      cy={dot.yA}
                      r={selected ? 5 : 3.5}
                      fill={colorA}
                    />
                    <circle
                      cx={dot.x}
                      cy={dot.yB}
                      r={selected ? 5 : 3.5}
                      fill={colorB}
                    />
                  </g>
                );
              })}
              {model.xTicks.map((tick) => (
                <text
                  key={`x-${tick.year}`}
                  x={tick.x}
                  y={height - 8}
                  textAnchor="middle"
                  fill={colors.textMuted}
                  fontSize={10}
                  fontFamily="IBM Plex Sans, sans-serif"
                >
                  {tick.year}
                </text>
              ))}
            </SvgChartCanvas>
            {model.dots.map((dot) => (
              <Pressable
                key={`hit-${dot.year}`}
                accessibilityRole="button"
                accessibilityLabel={`Year ${dot.year} snapshot`}
                onPress={() => setSelectedYear(dot.year)}
                style={[
                  styles.hit,
                  {
                    left: dot.x - hitWidth / 2,
                    width: hitWidth,
                    height: height - MARGIN.bottom,
                  },
                ]}
              />
            ))}
          </>
        )}
      </ChartHScroll>

      <Text style={styles.axisTitle}>Year</Text>

      {crossoverYear != null && (
        <Text style={styles.crossoverLabel}>Year {crossoverYear} — balances meet</Text>
      )}

      {snapshot && (
        <View style={styles.snapshot}>
          <Text style={styles.snapshotYear}>{snapshot.year}</Text>
          <View style={styles.snapshotRow}>
            <Text style={[styles.snapshotLabel, { color: colorA }]}>{labelA}</Text>
            <Text style={styles.snapshotValue}>{formatCurrency(snapshot.a)}</Text>
          </View>
          <View style={styles.snapshotRow}>
            <Text style={[styles.snapshotLabel, { color: colorB }]}>{labelB}</Text>
            <Text style={styles.snapshotValue}>{formatCurrency(snapshot.b)}</Text>
          </View>
          <View style={styles.snapshotRow}>
            <Text style={styles.snapshotLabel}>
              {gap >= 0 ? 'Investments ahead' : 'Debt still larger'}
            </Text>
            <Text
              style={[
                styles.snapshotValue,
                { color: gap >= 0 ? colors.accent : colors.warning },
              ]}
            >
              {formatCurrency(Math.abs(gap))}
            </Text>
          </View>
          {crossoverYear != null && (
            <Text style={styles.snapshotMeta}>
              {snapshot.year < crossoverYear
                ? `${crossoverYear - snapshot.year} years before crossover`
                : snapshot.year === crossoverYear
                  ? 'Crossover year'
                  : `${snapshot.year - crossoverYear} years after crossover`}
            </Text>
          )}
        </View>
      )}

      <View style={styles.legend}>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: colorA }]} />
          <Text style={styles.legendText}>{labelA}</Text>
          <Text style={styles.legendValue}>
            {formatK(seriesA[seriesA.length - 1]?.balance ?? 0)}
          </Text>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: colorB }]} />
          <Text style={styles.legendText}>{labelB}</Text>
          <Text style={styles.legendValue}>
            {formatK(seriesB[seriesB.length - 1]?.balance ?? 0)}
          </Text>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  hint: {
    ...typography.caption,
    color: colors.textMuted,
    marginBottom: spacing.xs,
  },
  chartArea: {
    backgroundColor: colors.surfaceElevated,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  yTick: {
    position: 'absolute',
    left: 4,
    width: MARGIN.left - 8,
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 10,
    fontVariant: ['tabular-nums'],
    textAlign: 'right',
  },
  hit: {
    position: 'absolute',
    top: 0,
    zIndex: 2,
    cursor: 'pointer' as const,
  },
  axisTitle: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
    marginTop: spacing.xs,
    textAlign: 'center',
  },
  crossoverLabel: {
    ...typography.caption,
    color: colors.accent,
    fontSize: 11,
    textAlign: 'center',
    marginTop: spacing.xs,
  },
  snapshot: {
    marginTop: spacing.sm,
    padding: spacing.md,
    borderRadius: radii.md,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderWidth: 1,
    borderColor: colors.glassBorder,
    gap: 6,
  },
  snapshotYear: {
    ...typography.headline,
    color: colors.text,
    fontSize: 18,
    marginBottom: 2,
  },
  snapshotRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  snapshotLabel: {
    ...typography.caption,
    color: colors.textMuted,
  },
  snapshotValue: {
    ...typography.caption,
    color: colors.text,
    fontWeight: '600',
    fontVariant: ['tabular-nums'],
  },
  snapshotMeta: {
    ...typography.caption,
    color: colors.textMuted,
    marginTop: 4,
    fontSize: 11,
  },
  legend: {
    flexDirection: 'row',
    gap: spacing.lg,
    marginTop: spacing.md,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  legendText: {
    ...typography.caption,
    color: colors.text,
    flex: 1,
  },
  legendValue: {
    ...typography.caption,
    color: colors.textMuted,
    fontVariant: ['tabular-nums'],
  },
  empty: {
    padding: spacing.lg,
    alignItems: 'center',
  },
  emptyText: {
    ...typography.body,
    color: colors.textMuted,
  },
});
